import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useSpeaker } from "@/lib/voice";
import { Plus, Search, ShieldAlert, Volume2, VolumeX, X } from "lucide-react";

const STEPS = [
  { icon: Plus, title: "Ask for help", text: "Tap the orange “Post a request” button. Say what you need, where, and when. It takes under a minute.", cta: "Post a request", to: "/post" },
  { icon: Search, title: "Choose a neighbour", text: "Look through our helpers. Green “Verified” badges mean the Kindred team checked their ID. Read reviews from other seekers.", cta: "See helpers", to: "/helpers" },
  { icon: ShieldAlert, title: "Stay safe", text: "A red “I need help now” button is always on your dashboard. Your family gets a message and nearby verified helpers are alerted.", cta: "Got it", to: null },
];

export default function GuidedTour({ user }) {
  const key = `kindred_tour_done_${user.id}`;
  const [step, setStep] = useState(0);
  const [open, setOpen] = useState(() => user.role === "seeker" && !localStorage.getItem(key));
  const [muted, setMuted] = useState(false);
  const { speak, stop, speaking } = useSpeaker();
  const nav = useNavigate();

  useEffect(() => {
    if (open && !muted) speak(`Step ${step + 1}. ${STEPS[step].title}. ${STEPS[step].text}`);
    return stop;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, step, muted]);

  const finish = (to) => { localStorage.setItem(key, "1"); stop(); setOpen(false); if (to) nav(to); };
  if (!open) return null;
  const s = STEPS[step];
  const Icon = s.icon;
  const last = step === STEPS.length - 1;

  return (
    <div data-testid="guided-tour" className="fixed inset-0 z-[60] bg-slate-900/60 flex items-center justify-center p-4">
      <div className="bg-[#FAF8F5] rounded-3xl max-w-xl w-full p-8 sm:p-10 shadow-2xl relative">
        <button data-testid="tour-skip-btn" onClick={() => finish(null)} aria-label="Skip tour" className="absolute top-4 right-4 w-12 h-12 rounded-full hover:bg-black/5 flex items-center justify-center text-slate-600"><X className="w-6 h-6" /></button>
        <p className="text-sm font-semibold text-[#2E7D6B] tracking-wide">WELCOME TO KINDRED · STEP {step + 1} OF 3</p>
        <div className="flex items-center gap-4 mt-4">
          <span className="w-16 h-16 rounded-2xl bg-[#2E7D6B] flex items-center justify-center shrink-0"><Icon className="w-8 h-8 text-white" /></span>
          <h2 data-testid="tour-step-title" className="text-3xl font-display font-bold text-slate-900">{s.title}</h2>
        </div>
        <p className="text-xl text-slate-700 mt-5 leading-relaxed">{s.text}</p>
        <div className="flex gap-2 mt-6">{STEPS.map((st, i) => <span key={st.title} className={`h-2.5 flex-1 rounded-full ${i <= step ? "bg-[#D96B43]" : "bg-[#E2D9CE]"}`} />)}</div>
        <div className="flex flex-wrap items-center gap-3 mt-8">
          <button data-testid="tour-next-btn" onClick={() => (last ? finish(null) : setStep(step + 1))}
            className="bg-[#D96B43] hover:bg-[#C25832] text-white text-lg font-semibold px-8 min-h-[56px] rounded-xl transition-colors">
            {last ? "Start using Kindred" : "Next"}
          </button>
          {s.to && <button data-testid="tour-cta-btn" onClick={() => finish(s.to)} className="border-2 border-[#2E7D6B] text-[#2E7D6B] text-lg font-semibold px-6 min-h-[56px] rounded-xl bg-white hover:bg-[#E9F3F0] transition-colors">{s.cta}</button>}
          <button data-testid="tour-mute-btn" onClick={() => { if (!muted) stop(); setMuted(!muted); }} aria-label={muted ? "Unmute" : "Mute"}
            className="ml-auto w-14 h-14 rounded-full border-2 border-[#E2D9CE] bg-white flex items-center justify-center text-slate-700">
            {muted ? <VolumeX className="w-6 h-6" /> : <Volume2 className={`w-6 h-6 ${speaking ? "text-[#2E7D6B]" : ""}`} />}
          </button>
        </div>
      </div>
    </div>
  );
}
